import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';


import { translate } from 'services/i18nService';
import ModalService from 'services/ModalService';
import track from 'services/tracking';
import FirebaseStore from 'stores/FirebaseStore';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import Button from 'components/Button/Button';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import {
  BROADCAST_STATUS,
  NEW_SHOWS_STATE,
} from 'enum/videoPlayer';
import allUserGroups from 'enum/userGroups';
import {
  generateID,
  isMobileRender,
} from 'utils/helpers';
import { RegistrationModalCaller } from 'pages/RegistrationModal/utils';


const IDS = {
  prefix: 'sMoRiShA',
  element: 'ShowInteractionButtons',
  goPrivate: 'goPrivate',
};

const makeId = generateID(IDS.prefix, IDS.element);

function ShowInteractionButtons(props) {
  const {
    broadcasterName,
    className,
    handlePrivateShowAction,
    setShowRegistrationModal,
    onOpenRequestPrivateShowDrawer,
    user,
  } = props;

  const {
    allowCamToCam,
    isPrivateShowRequestActive,
    newShowsState,
    privateShowBlocksOfTimeActive,
    privateShowMinMinutes,
    viewerPricePerMinute,
    togglePrivateShowRequest,
  } = FirebaseStore;

  const { user: { sfw }, isEventCarouselOpen } = UserStore;
  const { broadcastStatus, isPlayerBroadcastMode, realityShow } = ProfileStore;

  const prevPrivateShowMinMinutesRef = useRef();
  const prevPrivateShowBlocksOfTimeActiveRef = useRef();
  const prevViewerPricePerMinuteRef = useRef();

  const isGroupShowActive =
    newShowsState === NEW_SHOWS_STATE.INSIDE_GS ||
    newShowsState === NEW_SHOWS_STATE.BONUS_GS ||
    newShowsState === NEW_SHOWS_STATE.GS_SELLING_TICKETS ||
    newShowsState === NEW_SHOWS_STATE.GS_GOAL_REACHED ||
    newShowsState === NEW_SHOWS_STATE.PAUSED_GS;

  const isGoPrivateButtonVisible = (
    newShowsState !== NEW_SHOWS_STATE.PAUSED &&
    broadcastStatus !== BROADCAST_STATUS.ACCEPTED_INSIDE_PS &&
    broadcastStatus !== BROADCAST_STATUS.INSIDE_PS &&
    broadcastStatus !== BROADCAST_STATUS.SPYING &&
    !isGroupShowActive
  ) || Boolean(realityShow?.mainEvent);

  const isGuestBilling = user.accessLevel === ACCESS_LEVEL_NUM.GUEST_BILLING;
  const sellingGsTickets = newShowsState === NEW_SHOWS_STATE.GS_SELLING_TICKETS;

  const onOpenRequestPrivateShowModal = useCallback(() => {
    ModalService.showModal(
      ModalService.MODAL_KEYS.REQUEST_PRIVATE_SHOW_MODAL,
      { handlePrivateShowAction }
    );
  }, [handlePrivateShowAction]);

  const seeC2CToggle = useCallback(() => {
    const isC2CViewer = UserStore.hasCF(allUserGroups.CAM2CAM_VIEWER);
    const hasCam2Cam = isC2CViewer && allowCamToCam;
    if (hasCam2Cam) {
      track({
        eventname: 'toggleSeen',
        username: user?.username || '',
        room: broadcasterName,
      });
    }
  }, [allowCamToCam, broadcasterName, user?.username]);

  const onClick = useCallback(() => {
    if (realityShow?.mainEvent) {
      UserStore.setShowEventCarousel(!isEventCarouselOpen);

      return;
    }
    if (sellingGsTickets) {
      return;
    }

    if (isPrivateShowRequestActive) {
      handlePrivateShowAction('cancelRequest');
      togglePrivateShowRequest(false);

      return;
    }

    if (isGuestBilling) {
      ModalService.showModal(ModalService.MODAL_KEYS.GUEST_BILLING_MODAL, {
        close: ModalService.hideModal,
        show: true,
      });

      return;
    }

    if (!user.username) {
      setShowRegistrationModal?.(true, RegistrationModalCaller.SHOW_INTERACTION_BUTTONS);

      return;
    }

    if (isMobileRender() && onOpenRequestPrivateShowDrawer) {
      onOpenRequestPrivateShowDrawer();
    } else {
      onOpenRequestPrivateShowModal();
    }

    seeC2CToggle();
  }, [
    handlePrivateShowAction,
    isGuestBilling,
    isPrivateShowRequestActive,
    onOpenRequestPrivateShowModal,
    seeC2CToggle,
    sellingGsTickets,
    setShowRegistrationModal,
    togglePrivateShowRequest,
    user.username,
    onOpenRequestPrivateShowDrawer,
    realityShow,
    isEventCarouselOpen,
  ]);

  useEffect(() => {
    const prevPrivateShowMinMinutes = prevPrivateShowMinMinutesRef.current;
    const prevPrivateShowBlocksOfTimeActive = prevPrivateShowBlocksOfTimeActiveRef.current;
    const prevViewerPricePerMinute = prevViewerPricePerMinuteRef.current;

    const isNotSameMinMinutes = prevPrivateShowMinMinutes && prevPrivateShowMinMinutes !== privateShowMinMinutes;
    const isNotSamePricePerMinute = prevViewerPricePerMinute && prevViewerPricePerMinute !== viewerPricePerMinute;
    const isNotSameBlocksOfTimeActive = (
      prevPrivateShowBlocksOfTimeActive &&
      prevPrivateShowBlocksOfTimeActive !== privateShowBlocksOfTimeActive
    );

    if (
      isPrivateShowRequestActive &&
      (isNotSameMinMinutes ||
        isNotSameBlocksOfTimeActive ||
        isNotSamePricePerMinute)
    ) {
      togglePrivateShowRequest(false);
      onOpenRequestPrivateShowModal();
    }

    prevPrivateShowMinMinutesRef.current = privateShowMinMinutes;
    prevPrivateShowBlocksOfTimeActiveRef.current = privateShowBlocksOfTimeActive;
    prevViewerPricePerMinuteRef.current = viewerPricePerMinute;
  }, [
    isPrivateShowRequestActive,
    privateShowBlocksOfTimeActive,
    privateShowMinMinutes,
    onOpenRequestPrivateShowModal,
    togglePrivateShowRequest,
    viewerPricePerMinute,
  ]);

  const label = useMemo(() => {
    if (realityShow?.mainEvent) {
      if (isEventCarouselOpen) {
        return translate('v2profile.button.hideCams');
      }

      return translate('v2profile.button.showCams');
    }

    if (sellingGsTickets) {
      return translate('v2profile.button.buyTicket');
    }

    if (isPrivateShowRequestActive) {
      return translate('v2profile.button.cancel');
    }

    return translate('v2profile.button.goPrivate');
  }, [
    isPrivateShowRequestActive,
    sellingGsTickets,
    realityShow,
    isEventCarouselOpen,
  ]);

  if (isGoPrivateButtonVisible) {
    return (
      <Button
        className={className}
        disabled={sfw || isPlayerBroadcastMode}
        id={makeId(IDS.goPrivate)}
        label={label}
        onClick={onClick}
      />
    );
  }

  return null;
}

ShowInteractionButtons.propTypes = {
  broadcasterName: PropTypes.string.isRequired,
  handlePrivateShowAction: PropTypes.func.isRequired,
  setShowRegistrationModal: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  className: PropTypes.string,
  onOpenRequestPrivateShowDrawer: PropTypes.func,
};

ShowInteractionButtons.defaultProps = {
  className: undefined,
  onOpenRequestPrivateShowDrawer: undefined,
};

export default observer(ShowInteractionButtons);

